/**
 * A/B Test Calculation Utilities
 * 
 * Shared calculation functions for sample size and test duration calculators.
 * Used by both the standalone tool pages and the experiment creation wizard.
 */

// ============================================================================
// Statistical Helper Functions
// ============================================================================

/**
 * Rational approximation for inverse normal CDF (Abramowitz & Stegun 26.2.23)
 */
function rationalApproximation(t: number): number {
  const c = [2.515517, 0.802853, 0.010328]
  const d = [1.432788, 0.189269, 0.001308]
  return t - ((c[0] + c[1] * t + c[2] * t * t) / (1 + d[0] * t + d[1] * t * t + d[2] * t * t * t))
}

/**
 * Inverse of the standard normal cumulative distribution function.
 * Converts a probability to the corresponding z-score.
 */
export function normalCDFInverse(p: number): number {
  if (p <= 0 || p >= 1) {
    throw new RangeError('Probability must be between 0 and 1 (exclusive)')
  }
  if (p < 0.5) {
    return -rationalApproximation(Math.sqrt(-2.0 * Math.log(p)))
  }
  return rationalApproximation(Math.sqrt(-2.0 * Math.log(1 - p)))
}

/**
 * Z-score lookup table for common significance levels
 */
const Z_SCORE_TABLE: Record<string, number> = {
  '0.90': 1.645,
  '0.95': 1.96,
  '0.99': 2.576,
}

// ============================================================================
// Types
// ============================================================================

interface SampleSizeParams {
  /** Baseline conversion rate (0-100) */
  baseline: number
  /** Minimum detectable effect as relative percentage (0-100) */
  mde: number
  /** Statistical significance (0-100), default 95 */
  significance?: number
  /** Statistical power (0-100), default 80 */
  power?: number
}

export interface SampleSizeResult {
  /** Required sample size per variant */
  sampleSizePerVariant: number
  /** Total sample size for all variants (assumes 2 variants) */
  totalSampleSize: number
}

interface TestDurationParams {
  /** Average daily visitors */
  dailyVisitors: number
  /** Baseline conversion rate (0-100) */
  baseline: number
  /** Minimum detectable effect as relative percentage (0-100) */
  mde: number
  /** Statistical significance (0-100), default 95 */
  significance?: number
  /** Number of variants in the test, default 2 */
  variants?: number
  /** Traffic split percentage per variant (0-1), default 0.5 for 50/50 */
  trafficSplit?: number
}

export interface TestDurationResult {
  /** Recommended test duration in days (rounded up to nearest full week, min 7) */
  days: number
  /** Required sample size per variant */
  sampleSizePerVariant: number
  /** Total sample size needed */
  totalSampleSize: number
  /** Calendar date when results should be reviewed */
  reviewDate: Date
  /** Traffic sufficiency assessment */
  trafficAssessment: TrafficAssessment
}

export type TrafficSeverity = 'good' | 'warning' | 'critical'

export interface TrafficAssessment {
  severity: TrafficSeverity
  message: string
  /** True when estimated duration exceeds 180 days */
  isLongRunning: boolean
  /** Effective daily visitors allocated to the test */
  effectiveDailyVisitors: number
}

export interface DurationEstimate {
  days: number
  reviewDate: Date
  trafficAssessment: TrafficAssessment
}

// ============================================================================
// Sample Size Calculation
// ============================================================================

/**
 * Calculate required sample size per variant for an A/B test.
 * Uses the two-proportion z-test formula with unpooled variance estimate.
 */
export function calculateSampleSize(params: SampleSizeParams): SampleSizeResult {
  const {
    baseline,
    mde,
    significance = 95,
    power = 80,
  } = params

  const baselineRate = baseline / 100
  const mdeRate = mde / 100
  const significanceRate = significance / 100
  const powerRate = power / 100

  // z-scores
  const zAlpha = normalCDFInverse(1 - (1 - significanceRate) / 2)
  const zBeta = normalCDFInverse(powerRate)

  // Expected conversion rates for control vs. variant
  const p1 = baselineRate
  const p2 = baselineRate * (1 + mdeRate)

  // Two-proportion z-test sample size formula (unpooled)
  const sampleSizePerVariant = Math.ceil(
    Math.pow(
      zAlpha * Math.sqrt(2 * baselineRate * (1 - baselineRate)) +
      zBeta * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2)),
      2
    ) / Math.pow(p2 - p1, 2)
  )

  return {
    sampleSizePerVariant,
    totalSampleSize: sampleSizePerVariant * 2,
  }
}

// ============================================================================
// Test Duration Calculation
// ============================================================================

/**
 * Assess traffic sufficiency and generate actionable warnings.
 */
function assessTraffic(
  dailyVisitors: number,
  estimatedDays: number,
  trafficSplit: number = 0.5,
): TrafficAssessment {
  const effectiveDailyVisitors = dailyVisitors * trafficSplit

  if (estimatedDays > 180) {
    return {
      severity: 'critical',
      message: `Based on your current traffic (~${formatNumber(Math.round(dailyVisitors))} visitors/day), this test may take 6+ months to reach significance. Consider: increasing traffic, testing a larger effect, or choosing a higher-traffic page.`,
      isLongRunning: true,
      effectiveDailyVisitors,
    }
  }

  if (dailyVisitors < 100) {
    return {
      severity: 'warning',
      message: `Low traffic detected (~${formatNumber(Math.round(dailyVisitors))} visitors/day). Results may take longer to achieve statistical significance. Consider testing on higher-traffic pages.`,
      isLongRunning: false,
      effectiveDailyVisitors,
    }
  }

  if (estimatedDays > 90) {
    return {
      severity: 'warning',
      message: `This test will take approximately ${formatDuration(estimatedDays)}. Consider testing a larger effect size to shorten the duration.`,
      isLongRunning: false,
      effectiveDailyVisitors,
    }
  }

  return {
    severity: 'good',
    message: `Your traffic volume supports this test well. Expected duration: ${formatDuration(estimatedDays)}.`,
    isLongRunning: false,
    effectiveDailyVisitors,
  }
}

/**
 * Round days up to the nearest full week (minimum 7 days).
 * A/B tests should run in full-week increments to account for weekly traffic cycles.
 */
function roundToFullWeek(days: number): number {
  const minDays = 7
  const rounded = Math.ceil(days / 7) * 7
  return Math.max(rounded, minDays)
}

/**
 * Calculate recommended test duration for an A/B test.
 * Includes traffic assessment and review date calculation.
 */
export function calculateTestDuration(params: TestDurationParams): TestDurationResult {
  const {
    dailyVisitors,
    baseline,
    mde,
    significance = 95,
    variants = 2,
    trafficSplit,
  } = params

  const baselineRate = baseline / 100
  const mdeRate = mde / 100
  const significanceRate = significance / 100

  // Get z-score from lookup table or calculate
  const zAlpha = Z_SCORE_TABLE[significanceRate.toFixed(2)] || normalCDFInverse(1 - (1 - significanceRate) / 2)
  const zBeta = 0.842 // Fixed for 80% power

  // Expected conversion rates
  const p1 = baselineRate
  const p2 = baselineRate * (1 + mdeRate)
  const pBar = (p1 + p2) / 2

  // Sample size per variant (pooled variance formula)
  const sampleSizePerVariant = Math.ceil(
    (Math.pow(zAlpha + zBeta, 2) * pBar * (1 - pBar)) / Math.pow(p2 - p1, 2)
  )

  const totalSampleSize = sampleSizePerVariant * variants

  // Calculate split: default is equal split among variants
  const effectiveSplit = trafficSplit ?? (1 / variants)
  const visitorsPerVariantPerDay = dailyVisitors * effectiveSplit

  // Raw days needed, then round to full week
  const rawDays = visitorsPerVariantPerDay > 0
    ? Math.ceil(sampleSizePerVariant / visitorsPerVariantPerDay)
    : Infinity

  const days = rawDays === Infinity ? 365 : roundToFullWeek(rawDays)

  // Review date
  const reviewDate = new Date()
  reviewDate.setDate(reviewDate.getDate() + days)

  // Traffic assessment
  const trafficAssessment = assessTraffic(dailyVisitors, days, effectiveSplit)

  return {
    days,
    sampleSizePerVariant,
    totalSampleSize,
    reviewDate,
    trafficAssessment,
  }
}

/**
 * Estimate test duration from a pre-calculated sample size and daily traffic.
 * Useful when sample size is already known (e.g. from the sample size calculator).
 */
export function estimateDuration(
  totalSampleSize: number,
  dailyVisitors: number,
  trafficSplit: number = 0.5,
): DurationEstimate {
  const effectiveDaily = dailyVisitors * trafficSplit
  const rawDays = effectiveDaily > 0
    ? Math.ceil(totalSampleSize / (effectiveDaily * 2))
    : Infinity

  const days = rawDays === Infinity ? 365 : roundToFullWeek(rawDays)

  const reviewDate = new Date()
  reviewDate.setDate(reviewDate.getDate() + days)

  const trafficAssessment = assessTraffic(dailyVisitors, days, trafficSplit)

  return { days, reviewDate, trafficAssessment }
}

// ============================================================================
// Parameter Quality Assessment
// ============================================================================

export type ParameterQuality = 'good' | 'acceptable' | 'concerning'

export interface ParameterAssessment {
  quality: ParameterQuality
  hint: string
}

/**
 * Assess whether the MDE value is reasonable.
 */
export function assessMDE(mde: number): ParameterAssessment {
  if (mde >= 5 && mde <= 30) {
    return { quality: 'good', hint: 'This is a typical detectable effect range.' }
  }
  if (mde > 30 && mde <= 50) {
    return { quality: 'acceptable', hint: 'A large lift. Easy to detect, but only a big change will produce it.' }
  }
  if (mde > 50) {
    return { quality: 'concerning', hint: 'Very large effect. If realistic, a smaller sample may suffice.' }
  }
  if (mde >= 1 && mde < 5) {
    return { quality: 'concerning', hint: 'A very small lift needs a very large sample.' }
  }
  return { quality: 'acceptable', hint: '' }
}

/**
 * Assess whether the baseline conversion rate is reasonable.
 */
export function assessBaseline(baseline: number): ParameterAssessment {
  if (baseline >= 1 && baseline <= 20) {
    return { quality: 'good', hint: 'Typical conversion rate range for most sites.' }
  }
  if (baseline > 20 && baseline <= 50) {
    return { quality: 'acceptable', hint: 'Higher than average. Check it matches your real data.' }
  }
  if (baseline > 50) {
    return { quality: 'concerning', hint: 'Unusually high conversion rate. Double-check your measurement.' }
  }
  if (baseline > 0 && baseline < 1) {
    return { quality: 'acceptable', hint: 'A low conversion rate needs a lot of traffic.' }
  }
  return { quality: 'acceptable', hint: '' }
}

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format a duration in days to a human-readable string.
 */
export function formatDuration(days: number): string {
  if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'}`
  } else if (days < 30) {
    const weeks = Math.floor(days / 7)
    const remainingDays = days % 7
    const weekStr = `${weeks} week${weeks === 1 ? '' : 's'}`
    const dayStr = remainingDays ? ` and ${remainingDays} day${remainingDays === 1 ? '' : 's'}` : ''
    return weekStr + dayStr
  } else {
    const months = Math.floor(days / 30)
    const remainingDays = days % 30
    const monthStr = `${months} month${months === 1 ? '' : 's'}`
    const dayStr = remainingDays ? ` and ${remainingDays} day${remainingDays === 1 ? '' : 's'}` : ''
    return monthStr + dayStr
  }
}

/**
 * Format a number with commas for display.
 */
export function formatNumber(num: number): string {
  return num.toLocaleString()
}

/**
 * Format a Date object to a locale date string.
 */
export function formatReviewDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
